import type {
  CaptionAuthoredStyle,
  CaptionRevealMode,
  EditOp,
  SourcePack,
} from "@desk/core";
import {
  captionProgress01,
  captionRevealVisibleCount,
  normalizeCaptionPresentation,
} from "@desk/core";

export {
  captionTextFromTokens,
  isGrammarValid,
  nextOpId,
} from "./edit-helpers.js";

export type PreviewCaptionToken = {
  id: string;
  text: string;
  emphasized: boolean;
};

export type PreviewCaptionState = {
  visible: boolean;
  fullText: string;
  tokens: PreviewCaptionToken[];
  style: CaptionAuthoredStyle;
  revealMode: CaptionRevealMode;
  scaleMilli: number;
  anchorXMilli: number;
  anchorYMilli: number;
  /** Presentation-only rotation (millidegrees, [0, 360000)) — never enters the
   *  scored edit graph; defaults to 0 (upright), overridden by EditorScreen's
   *  on-video transform handle (mirrors how `captionPos` overrides the anchor
   *  without touching @desk/core). See `clampStickerRotationMilli`. */
  rotationMilli: number;
  /** 0–1 pop scale factor for revealMode=pop before fully shown. */
  popScale: number;
  /** Cosmetic reaction-tag glyph rendered UNDER the caption text (shell-only). */
  reactionEmoji?: string;
};

/** Resolve the active caption overlay at a source tick (deterministic). */
export function resolvePreviewCaption(
  pack: SourcePack,
  captions: Extract<EditOp, { kind: "text" }>[],
  sourceTick: number,
): PreviewCaptionState {
  const active = captions.filter(
    (c) => sourceTick >= c.startTick && sourceTick < c.endTick,
  );
  const last = active[active.length - 1];
  if (!last) {
    return {
      visible: false,
      fullText: "",
      tokens: [],
      style: "documentary",
      revealMode: "static",
      scaleMilli: 1000,
      anchorXMilli: 500,
      anchorYMilli: 820,
      rotationMilli: 0,
      popScale: 1,
    };
  }

  const presentation = normalizeCaptionPresentation(last);
  const byId = new Map(pack.captionVocabulary.tokens.map((t) => [t.id, t.text]));
  const progress = captionProgress01(sourceTick, last.startTick, last.endTick);
  const visibleCount = captionRevealVisibleCount(
    last.tokenIds.length,
    presentation.revealMode,
    progress,
  );
  const emph = new Set(presentation.emphasizedTokenIds);
  const tokens: PreviewCaptionToken[] = last.tokenIds
    .slice(0, visibleCount)
    .map((id) => ({
      id,
      text: byId.get(id) ?? "?",
      emphasized: emph.has(id),
    }));
  const fullText = last.tokenIds.map((id) => byId.get(id) ?? "?").join(" ");
  let popScale = 1;
  if (presentation.revealMode === "pop") {
    if (visibleCount === 0) popScale = 0.6;
    else {
      const popProgress = Math.max(0, Math.min(1, (progress - 0.12) / 0.2));
      popScale = 0.75 + popProgress * 0.25;
    }
  }

  return {
    visible: tokens.length > 0,
    fullText,
    tokens,
    style: presentation.style,
    revealMode: presentation.revealMode,
    scaleMilli: presentation.scaleMilli,
    anchorXMilli: presentation.anchorXMilli,
    anchorYMilli: presentation.anchorYMilli,
    // @desk/core has no caption rotation field; it is purely a game-side
    // presentation lever (see `rotationMilli` doc above) applied later by
    // EditorScreen, same as the drag position override.
    rotationMilli: 0,
    popScale,
  };
}

export function packAudioAssets(pack: SourcePack) {
  return pack.assets.filter((a) => a.kind === "audio");
}

export function packEndTick(pack: SourcePack): number {
  return pack.beats.reduce((m, b) => Math.max(m, b.endTick), 0);
}
